<template>
  <div class="video-html" :class="$attrs.class">
    <video
      ref="videoElement"
      :src="currentVideoSrc"
      :controls="currentControls"
      :muted="currentMuted"
      :loop="currentLoop"
      playsinline
      :autoplay="currentAutoplay"
      preload="metadata"
      @pause="isVideoPlaying = false"
      @play="isVideoPlaying = true"
    />
    <!-- Play -->
    <button 
      v-show="showPlayButton"
      class="play-overlay button-round-play"
      @click="playFullVideo"
    >
      <span>Play</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'WidgetVideo',
}
</script>

<script setup>
const props = defineProps({
  videoUrl: String,
  videoPreview: String,
  fullVideo: String,
  autoplay: Boolean,
  showPlayButton: Boolean,
  muted: {
    type: Boolean,
    default: false,
  },
  preload: {
    type: String,
    default: 'none',
  },
  controls: {
    type: Boolean,
    default: false,
  },
  loop: Boolean,
  playInViewport: {
    // Se true e cambio tab del browser il video si stoppa
    type: Boolean,
    default: false,
  },
  forcePlay: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['play', 'pause'])

const videoElement = ref(null)
const isPlayingFull = ref(false)
const hasStartedFull = ref(false) 
const isVideoPlaying = ref(false)
let observer = null

const showPlayButton = computed(() => {
  if (!props.showPlayButton) return false
  
  if (!isPlayingFull.value) return true
  
  if (isPlayingFull.value && !isVideoPlaying.value) return true
  
  return false
})

const currentVideoSrc = computed(() => {
  const src = (props.videoPreview || props.fullVideo) 
    ? (isPlayingFull.value ? props.fullVideo : props.videoPreview)
    : props.videoUrl
  // console.log('currentVideoSrc:', src)
  return src
})

const currentControls = computed(() => {
  if (props.videoPreview || props.fullVideo) {
    return isPlayingFull.value ? true : false
  }
  return props.controls
})

const currentMuted = computed(() => {
  if (props.videoPreview || props.fullVideo) {
    return isPlayingFull.value ? false : true
  }
  return props.muted
})

const currentLoop = computed(() => {
  if (props.videoPreview || props.fullVideo) {
    return isPlayingFull.value ? props.loop : true
  }
  return props.loop
})

const currentAutoplay = computed(() => {
  if (props.videoPreview || props.fullVideo) {
    return isPlayingFull.value ? props.autoplay : true
  }
  return props.autoplay
})

const playFullVideo = () => {
  if (props.fullVideo) {
    isPlayingFull.value = true
    hasStartedFull.value = true
    nextTick(() => {
      if (videoElement.value) {
        videoElement.value.play()
      }
    })
  } else if (videoElement.value) {
    videoElement.value.play()
  }
}

const resetFullVideo = () => {
  if (videoElement.value) {
    videoElement.value.pause()
    videoElement.value.currentTime = 0
  }
  isPlayingFull.value = false
  hasStartedFull.value = false
}

onMounted(() => {
  if (props.playInViewport) {
    window.addEventListener('scroll', checkVideoInViewport)
    checkVideoInViewport()
  }
})

watch(
  () => props.forcePlay,
  (forcePlay) => {
    if (forcePlay) {
      videoElement.value?.play()
    } else {
      videoElement.value?.pause()
    }
  },
)

onUnmounted(() => {
  window.removeEventListener('scroll', checkVideoInViewport)
})

const checkVideoInViewport = () => {
  if (!videoElement.value) return
  
  const rect = videoElement.value.getBoundingClientRect()
  const isInViewport = rect.top < window.innerHeight && rect.bottom > 0
  
  if (isInViewport) {
    if (!isPlayingFull.value && videoElement.value.paused) {
      videoElement.value.play()
    }
  } else {
    if (!videoElement.value.paused) {
      videoElement.value.pause()
    }
  }
}

defineExpose({
  videoElement,
  resetFullVideo,
})
</script>

<style lang="scss">
@import './style.scss';
</style>
